import { Stack, Typography, useTheme } from "@mui/material";
import huduLogo from "assets/images/integration/hudu.png";
import { CopyIcon, InfoIcon, Input } from "components";
import { useGetHuduAuthConfig } from "controller";
import { useEffect } from "react";
import toast from "react-hot-toast";
import { UpsertHuduAuthConfigInput } from "shared/types/integrations";
export const ConnectHuduAccount = ({
  apiKey,
  setApiKey,
  accountUrl,
  setAccountUrl,
  errorMessage,
  setErrorMessage,
}: {
  apiKey: string;
  setApiKey: any;
  accountUrl: string;
  setAccountUrl: any;
  upsertHuduAuthConfig: (data: UpsertHuduAuthConfigInput) => Promise<void>;
  isUpsertHuduAuthConfigLoading: boolean;
  errorMessage: string;
  setErrorMessage: (errorMessage: string) => void;
}) => {
  const theme = useTheme();

  const { data: huduAuthConfig } = useGetHuduAuthConfig();

  const WEBHOOK_URL = "https://hudu-api.deskday.ai/api/sync/webhook";
  const WEBHOOK_PAYLOAD = `{
    record_name: $RECORD_NAME,
    record_type: $RECORD_TYPE,
    hudu_url: "$HUDU_URL",
    action: $ACTION,
    record_id: $RECORD_ID,
  }`;

  const copyValue = (value: string) => {
    navigator.clipboard.writeText(value);
    toast.success("Copied to clipboard");
  };

  useEffect(() => {
    if (errorMessage && apiKey) {
      setErrorMessage("");
    }
  }, [apiKey]);

  return (
    <Stack gap="24px" py="24px" maxHeight="100%" width="100%">
      <Stack px="24px" gap="24px">
        <Stack gap="8px">
          <img
            src={huduLogo}
            alt="hudu logo"
            style={{
              height: "auto",
              width: "128.49px",
            }}
          />
          <Typography variant="body-small" color="lightBg.low">
            Helena will parse your Hudu docs to deliver faster, more accurate
            answers and improve native search.
          </Typography>
        </Stack>
        <Stack gap="12px">
          {" "}
          <Input
            label="Hudu Account URL"
            placeholder="eg: https://your.hudu.domain"
            name="hudu_account_url"
            value={accountUrl}
            onChange={(e) => setAccountUrl(e.target.value)}
            errorMessage={errorMessage}
            disabled={huduAuthConfig?.data?.hudu_url}
            autoComplete="off"
          />
          <Input
            label="Hudu API Key"
            placeholder="Enter API key"
            name="hudu_api_key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            errorMessage={errorMessage}
            type="password"
            disabled={huduAuthConfig?.data?.has_api_key}
            autoComplete="off"
          />
        </Stack>
        <Typography
          variant="body-small"
          color={theme.palette.lightBg.low}
          mt="-22px"
        >
          *To generate your API key, go to Admin → API Keys and click Create API
          Key in your Hudu account.
        </Typography>
        <Stack
          border={`1px solid ${theme.palette.neutral["015"]}`}
          borderRadius="8px"
          p="16px"
          gap="24px"
        >
          <Stack>
            <Typography variant="h6">
              Configure the Webhook in your Hudu account
            </Typography>
            <Typography variant="body-small" color={theme.palette.lightBg.low}>
              Copy the Webhook URL and Payload and paste them into your Hudu
              alert configuration.
            </Typography>
          </Stack>
          <Stack gap="12px">
            <Input
              name="webhook_url"
              label="Webhook URL"
              value={WEBHOOK_URL}
              disabled={true}
              hideHelperBox={true}
              InputProps={{
                endAdornment: (
                  <CopyIcon
                    fontSize="small"
                    sx={{ cursor: "pointer" }}
                    onClick={() => copyValue(WEBHOOK_URL)}
                  />
                ),
              }}
            />
            <Input
              name="webhook_payload"
              label="WebhookPayload"
              value={WEBHOOK_PAYLOAD}
              disabled={true}
              hideHelperBox={true}
              InputProps={{
                endAdornment: (
                  <CopyIcon
                    fontSize="small"
                    sx={{ cursor: "pointer" }}
                    onClick={() => copyValue(WEBHOOK_PAYLOAD)}
                  />
                ),
              }}
            />
          </Stack>
        </Stack>
        <SetupInstructions />
      </Stack>
    </Stack>
  );
};

const SetupInstructions = () => {
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      gap="8px"
      p="12px"
      borderRadius="8px"
      border={"1px solid #98c6f1"}
      bgcolor="#E8F3FE"
    >
      <InfoIcon
        style={{
          width: "24px",
          height: "24px",
        }}
        color="info"
      />
      <Stack gap="8px">
        <Typography variant="body-small">
          To set up Alerts in Hudu, add the Webhook URL and Webhook Payload
          shown above to your Hudu alerts configuration. This step is necessary
          for syncing documents to DeskDay. On Hudu Account,
        </Typography>
        <Stack component="ol" pl="16px">
          <Typography variant="body-small" component="li">
            Go to Admin → Alerts and click Add New Alert
          </Typography>
          <Typography variant="body-small" component="li">
            Enter a convenient name (e.g., DeskDay Sync Alert)
          </Typography>
          <Typography variant="body-small" component="li">
            Under Events, select Record Created / Updated / Deleted
          </Typography>
          <Typography variant="body-small" component="li">
            Enable Configure Webhook
          </Typography>
          <Typography variant="body-small" component="li">
            Add the Webhook URL and Webhook Payload provided above
          </Typography>
          <Typography variant="body-small" component="li">
            Click Create Alert
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};
